import * as React from "react";
import { Navigate } from "react-router";
import * as Dtos from "./dtos/Tmd.dtos";
import { AppRouteObject } from "./hooks/useRouteHelp";
import AppLayout from "./components/layouts/AppLayout";
import OverlayLoading from "src/components/common/OverlayLoading/OverlayLoading";
import RouteErrorBoundary from "./components/RouteErrorBoundary";

// Lazy-loaded components
const CommitteeMeetingAttendancePage = React.lazy(() => import("./containers/Committees/CommitteeMeetingAttendance/CommitteeMeetingAttendancePage").then(m => ({ default: m.default })));
const CommitteeMeetingAttendancesPage = React.lazy(() => import("./containers/Committees/CommitteeMeetingAttendances/CommitteeMeetingAttendancesPage").then(m => ({ default: m.default })));
const CommitteeMeetingPage = React.lazy(() => import("./containers/Committees/CommitteeMeeting/CommitteeMeetingPage").then(m => ({ default: m.default })));
const CommitteeMeetingsPage = React.lazy(() => import("./containers/Committees/CommitteeMeetings/CommitteeMeetingsPage").then(m => ({ default: m.default })));
const CommitteeMemberPage = React.lazy(() => import("./containers/Committees/CommitteeMember/CommitteeMemberPage").then(m => ({ default: m.default })));
const CommitteeMemberRolePage = React.lazy(() => import("./containers/Committees/CommitteeMemberRole/CommitteeMemberRolePage").then(m => ({ default: m.default })));
const CommitteeMemberRolesPage = React.lazy(() => import("./containers/Committees/CommitteeMemberRoles/CommitteeMemberRolesPage").then(m => ({ default: m.default })));
const CommitteeMembersPage = React.lazy(() => import("./containers/Committees/CommitteeMembers/CommitteeMembersPage").then(m => ({ default: m.default })));
const CommitteePage = React.lazy(() => import("./containers/Committees/Committee/CommitteePage").then(m => ({ default: m.default })));
const CommitteeRegularInviteePage = React.lazy(() => import("./containers/Committees/CommitteeRegularInvitee/CommitteeRegularInviteePage").then(m => ({ default: m.default })));
const CommitteeRegularInviteesPage = React.lazy(() => import("./containers/Committees/CommitteeRegularInvitees/CommitteeRegularInviteesPage").then(m => ({ default: m.default })));
const CommitteesPage = React.lazy(() => import("./containers/Committees/Committees/CommitteesPage").then(m => ({ default: m.default })));
const CountriesPage = React.lazy(() => import("./containers/Lookups/Countries/CountriesPage").then(m => ({ default: m.default })));
const CountryPage = React.lazy(() => import("./containers/Lookups/Country/CountryPage").then(m => ({ default: m.default })));
const DashboardPage = React.lazy(() => import("./containers/Dashboard/DashboardPage").then(m => ({ default: m.default })));
const DiscussionPage = React.lazy(() => import("./containers/Projects/Discussion/DiscussionPageSWR").then(m => ({ default: m.default })));
const DiscussionsPage = React.lazy(() => import("./containers/Projects/Discussions/DiscussionsPageSWR").then(m => ({ default: m.default })));
const ErrorPage = React.lazy(() => import("./containers/Error/errorPage").then(m => ({ default: m.default })));
const FellowshipPage = React.lazy(() => import("./containers/Personnel/Fellowship/FellowshipEditorSWR").then(m => ({ default: m.default })));
const FellowshipsPage = React.lazy(() => import("./containers/Personnel/Fellowship/FellowshipsSWRPage").then(m => ({ default: m.default })));
const GeneralSettingsPage = React.lazy(() => import("./containers/Settings/GeneralSettings/GeneralSettingsPage").then(m => ({ default: m.default })));
const GroupContractPage = React.lazy(() => import("./containers/Groups/GroupContract/GroupContractPage").then(m => ({ default: m.default })));
const GroupFinancePage = React.lazy(() => import("./containers/Groups/GroupFinance/GroupFinancePage").then(m => ({ default: m.default })));
const GroupItemTemplateCategoryPage = React.lazy(() => import("./containers/GroupSettings/GroupItemTemplateCategory/GroupItemTemplateCategoryPage").then(m => ({ default: m.default })));
const GroupItemTemplateCategorysPage = React.lazy(() => import("./containers/GroupSettings/GroupItemTemplateCategorys/GroupItemTemplateCategorysPage").then(m => ({ default: m.default })));
const GroupItemTemplatePage = React.lazy(() => import("./containers/GroupSettings/GroupItemTemplate/GroupItemTemplatePage").then(m => ({ default: m.default })));
const GroupItemTemplatesPage = React.lazy(() => import("./containers/GroupSettings/GroupItemTemplates/GroupItemTemplatesPage").then(m => ({ default: m.default })));
const GroupItemsPage = React.lazy(() => import("./containers/Groups/GroupItems/GroupItemsPageSWR").then(m => ({ default: m.default })));
const GroupNotesPage = React.lazy(() => import("./containers/Groups/GroupNotes/GroupNotesPage").then(m => ({ default: m.default })));
const GroupPage = React.lazy(() => import("./containers/Groups/Group/GroupPage").then(m => ({ default: m.default })));
const GroupPersonnelPage = React.lazy(() => import("./containers/Groups/GroupPersonnel/GroupPersonnelPage").then(m => ({ default: m.default })));
const GroupPersonnelsPage = React.lazy(() => import("./containers/Groups/GroupPersonnels/GroupPersonnelsPage").then(m => ({ default: m.default })));
const GroupSettingsPage = React.lazy(() => import("./containers/Settings/GroupSettings/GroupSettingsPage").then(m => ({ default: m.default })));
const GroupTrialsPage = React.lazy(() => import("./containers/Groups/GroupTrials/GroupTrialsPage").then(m => ({ default: m.default })));
const GroupsPage = React.lazy(() => import("./containers/Groups/Groups/GroupsPage").then(m => ({ default: m.default })));
const HealthDistrictPage = React.lazy(() => import("./containers/Settings/HealthDistrict/HealthDistrictPageSWR").then(m => ({ default: m.default })));
const HealthDistrictsPage = React.lazy(() => import("./containers/Settings/HealthDistrict/HealthDistrictsPageSWR").then(m => ({ default: m.default })));
const HrecPage = React.lazy(() => import("./containers/Settings/HREC/HrecPage").then(m => ({ default: m.default })));
const HrecsPage = React.lazy(() => import("./containers/Settings/HREC/HrecsPage").then(m => ({ default: m.default })));
const InstitutionCodePage = React.lazy(() => import("./containers/Institutions/InstitutionCode/InstitutionCodePage").then(m => ({ default: m.default })));
const InstitutionCodesPage = React.lazy(() => import("./containers/Institutions/InstitutionCodes/InstitutionCodesPage").then(m => ({ default: m.default })));
const InstitutionCodingPage = React.lazy(() => import("./containers/InstitutionLookups/InstitutionCoding/InstitutionCodingPage").then(m => ({ default: m.default })));
const InstitutionCodingsPage = React.lazy(() => import("./containers/InstitutionLookups/InstitutionCodings/InstitutionCodingsPage").then(m => ({ default: m.default })));
const InstitutionContractPage = React.lazy(() => import("./containers/Institutions/InstitutionContract/InstitutionContractPage").then(m => ({ default: m.default })));
const InstitutionFinancePage = React.lazy(() => import("./containers/Institutions/InstitutionFinance/InstitutionFinancePage").then(m => ({ default: m.default })));
const InstitutionItemTemplateCategoriesPage = React.lazy(() => import("./containers/InstitutionLookups/InstitutionItemTemplateCategories/InstitutionItemTemplateCategoriesPage").then(m => ({ default: m.default })));
const InstitutionItemTemplateCategoryPage = React.lazy(() => import("./containers/InstitutionLookups/InstitutionItemTemplateCategory/InstitutionItemTemplateCategoryPage").then(m => ({ default: m.default })));
const InstitutionItemTemplatePage = React.lazy(() => import("./containers/InstitutionLookups/InstitutionItemTemplate/InstitutionItemTemplatePage").then(m => ({ default: m.default })));
const InstitutionItemTemplatesPage = React.lazy(() => import("./containers/InstitutionLookups/InstitutionItemTemplates/InstitutionItemTemplatesPage").then(m => ({ default: m.default })));
const InstitutionItemsPage = React.lazy(() => import("./containers/Institutions/InstitutionItems/InstitutionItemsPageSWR").then(m => ({ default: m.default })));
const InstitutionLaboratoriesPage = React.lazy(() => import("./containers/Institutions/InstitutionLaboratories/InstitutionLaboratoriesPage").then(m => ({ default: m.default })));
const InstitutionLaboratoryPage = React.lazy(() => import("./containers/Institutions/InstitutionLaboratory/InstitutionLaboratoryPage").then(m => ({ default: m.default })));
const InstitutionNotesPage = React.lazy(() => import("./containers/Institutions/InstitutionNotes/InstitutionNotesPage").then(m => ({ default: m.default })));
const InstitutionPage = React.lazy(() => import("./containers/Institutions/Institution/InstitutionPageSWR").then(m => ({ default: m.default })));
const InstitutionPersonnelPage = React.lazy(() => import("./containers/Institutions/InstitutionPersonnel/InstitutionPersonnelPage").then(m => ({ default: m.default })));
const InstitutionPersonnelsPage = React.lazy(() => import("./containers/Institutions/InstitutionPersonnels/InstitutionPersonnelsPage").then(m => ({ default: m.default })));
const InstitutionProcedurePage = React.lazy(() => import("./containers/Institutions/InstitutionProcedure/InstitutionProcedurePage").then(m => ({ default: m.default })));
const InstitutionProceduresPage = React.lazy(() => import("./containers/Institutions/InstitutionProcedures/InstitutionProceduresPage").then(m => ({ default: m.default })));
const InstitutionSettingsPage = React.lazy(() => import("./containers/Settings/InstitutionSettings/InstitutionSettingsPage").then(m => ({ default: m.default })));
const InstitutionTrialsPage = React.lazy(() => import("./containers/Institutions/InstitutionTrials/InstitutionTrialsPage").then(m => ({ default: m.default })));
const InstitutionTypePage = React.lazy(() => import("./containers/InstitutionLookups/InstitutionType/InstitutionTypePage").then(m => ({ default: m.default })));
const InstitutionTypesPage = React.lazy(() => import("./containers/InstitutionLookups/InstitutionTypes/InstitutionTypesPage").then(m => ({ default: m.default })));
const InstitutionsPage = React.lazy(() => import("./containers/Institutions/Institutions/InstitutionsPage").then(m => ({ default: m.default })));
const InterestPage = React.lazy(() => import("./containers/Lookups/Interest/InterestPage").then(m => ({ default: m.default })));
const InterestsPage = React.lazy(() => import("./containers/Lookups/Interests/InterestsPage").then(m => ({ default: m.default })));
const ItemComponentPage = React.lazy(() => import("./containers/Settings/ItemComponent/ItemComponentPageSWR").then(m => ({ default: m.default })));
const ItemComponentTypePage = React.lazy(() => import("./containers/Lookups/ItemComponentType/ItemComponentTypePage").then(m => ({ default: m.default })));
const ItemComponentTypesPage = React.lazy(() => import("./containers/Lookups/ItemComponentTypes/ItemComponentTypesPage").then(m => ({ default: m.default })));
const ItemComponentsPage = React.lazy(() => import("./containers/Settings/ItemComponents/ItemComponentsPage").then(m => ({ default: m.default })));
const KeyContactPage = React.lazy(() => import("./containers/InstitutionLookups/KeyContact/KeyContactPage").then(m => ({ default: m.default })));
const KeyContactsPage = React.lazy(() => import("./containers/InstitutionLookups/KeyContacts/KeyContactsPage").then(m => ({ default: m.default })));
const LabContractPage = React.lazy(() => import("./containers/Labs/LabContract/LabContractPage").then(m => ({ default: m.default })));
const LabDepartmentPage = React.lazy(() => import("./containers/Labs/LabDepartment/LabDepartmentPage").then(m => ({ default: m.default })));
const LabDepartmentsPage = React.lazy(() => import("./containers/Labs/LabDepartments/LabDepartmentsPage").then(m => ({ default: m.default })));
const LabFinancePage = React.lazy(() => import("./containers/Labs/LabFinance/LabFinancePage").then(m => ({ default: m.default })));
const LabInstitutionPage = React.lazy(() => import("./containers/Labs/LabInstitution/LabInstitutionPage").then(m => ({ default: m.default })));
const LabInstitutionsPage = React.lazy(() => import("./containers/Labs/LabInstitutions/LabInstitutionsPage").then(m => ({ default: m.default })));
const LabPage = React.lazy(() => import("./containers/Labs/Lab/LabPage").then(m => ({ default: m.default })));
const LabPersonnelPage = React.lazy(() => import("./containers/Labs/LabPersonnel/LabPersonnelPage").then(m => ({ default: m.default })));
const LabPersonnelsPage = React.lazy(() => import("./containers/Labs/LabPersonnels/LabPersonnelsPage").then(m => ({ default: m.default })));
const LabsPage = React.lazy(() => import("./containers/Labs/Labs/LabsPage").then(m => ({ default: m.default })));
const MembershipTypePage = React.lazy(() => import("./containers/Lookups/MembershipType/MembershipTypePage").then(m => ({ default: m.default })));
const MembershipTypesPage = React.lazy(() => import("./containers/Lookups/MembershipTypes/MembershipTypesPage").then(m => ({ default: m.default })));
const MonitoringDocumentPage = React.lazy(() => import("./containers/Monitoring/MonitoringDocument/MonitoringDocumentPage").then(m => ({ default: m.default })));
const MonitoringDocumentsPage = React.lazy(() => import("./containers/Monitoring/MonitoringDocuments/MonitoringDocumentsPage").then(m => ({ default: m.default })));
const MonitoringIssueDocumentPage = React.lazy(() => import("./containers/Monitoring/MonitoringIssueDocument/MonitoringIssueDocumentPage").then(m => ({ default: m.default })));
const MonitoringIssueDocumentsPage = React.lazy(() => import("./containers/Monitoring/MonitoringIssueDocuments/MonitoringIssueDocumentsPage").then(m => ({ default: m.default })));
const MonitoringIssuePage = React.lazy(() => import("./containers/Monitoring/MonitoringIssue/MonitoringIssuePage").then(m => ({ default: m.default })));
const MonitoringIssuesPage = React.lazy(() => import("./containers/Monitoring/MonitoringIssues/MonitoringIssuesPage").then(m => ({ default: m.default })));
const MonitoringPage = React.lazy(() => import("./containers/Monitoring/Monitoring/MonitoringPage").then(m => ({ default: m.default })));
const MonitoringsPage = React.lazy(() => import("./containers/Monitoring/Monitorings/MonitoringsPage").then(m => ({ default: m.default })));
const MyTrialsPage = React.lazy(() => import("./containers/MyTrials/MyTrials/MyTrialsPage").then(m => ({ default: m.default })));
const NotePage = React.lazy(() => import("./containers/Notes/Note/NotePageSWR").then(m => ({ default: m.default })));
const NoteTypePage = React.lazy(() => import("./containers/Lookups/NoteType/NoteTypePage").then(m => ({ default: m.default })));
const NoteTypesPage = React.lazy(() => import("./containers/Lookups/NoteTypes/NoteTypesPage").then(m => ({ default: m.default })));
const PersonnelActiveDirectoryPage = React.lazy(() => import("./containers/Personnel/PersonnelActiveDirectory/PersonnelActiveDirectoryEditor").then(m => ({ default: m.default })));
const PersonnelByRolesPage = React.lazy(() => import("./containers/Personnel/PersonnelByRoles/PersonnelByRolesPage").then(m => ({ default: m.default })));
const PersonnelNotesPage = React.lazy(() => import("./containers/Personnel/PersonnelNotes/PersonnelNotesPage").then(m => ({ default: m.default })));
const PersonnelPage = React.lazy(() => import("./containers/Personnel/Personnel/PersonnelEditor").then(m => ({ default: m.default })));
const PersonnelPermissionsPage = React.lazy(() => import("./containers/Personnel/PersonnelPermissions/PersonnelPermissionsPage").then(m => ({ default: m.default })));
const PersonnelRolePage = React.lazy(() => import("./containers/Personnel/PersonnelRole/PersonnelRolePage").then(m => ({ default: m.default })));
const PersonnelRolesPage = React.lazy(() => import("./containers/Personnel/PersonnelRoles/PersonnelRolesPage").then(m => ({ default: m.default })));
const PersonnelTrainingCertificationPage = React.lazy(() => import("./containers/Personnel/PersonnelTrainingCertification/PersonnelTrainingCertificationPage").then(m => ({ default: m.default })));
const PersonnelsPage = React.lazy(() => import("./containers/Personnel/Personnels/PersonnelsPage").then(m => ({ default: m.default })));
const ProfessionalDisciplinePage = React.lazy(() => import("./containers/Lookups/ProfessionalDiscipline/ProfessionalDisciplinePage").then(m => ({ default: m.default })));
const ProfessionalDisciplinesPage = React.lazy(() => import("./containers/Lookups/ProfessionalDisciplines/ProfessionalDisciplinesPage").then(m => ({ default: m.default })));
const ProjectInvestigatorPage = React.lazy(() => import("./containers/Projects/ProjectInvestigator/ProjectInvestigatorPage").then(m => ({ default: m.default })));
const ProjectItemTemplateCategoryPage = React.lazy(() => import("./containers/ProjectLookups/ProjectItemTemplateCategory/ProjectItemTemplateCategoryPage").then(m => ({ default: m.default })));
const ProjectItemTemplateCategorysPage = React.lazy(() => import("./containers/ProjectLookups/ProjectItemTemplateCategorys/ProjectItemTemplateCategorysPage").then(m => ({ default: m.default })));
const ProjectItemTemplatePage = React.lazy(() => import("./containers/ProjectLookups/ProjectItemTemplate/ProjectItemTemplatePage").then(m => ({ default: m.default })));
const ProjectItemTemplatesPage = React.lazy(() => import("./containers/ProjectLookups/ProjectItemTemplates/ProjectItemTemplatesPage").then(m => ({ default: m.default })));
const ProjectItemsPage = React.lazy(() => import("./containers/Projects/ProjectItems/ProjectItemsPageSWR").then(m => ({ default: m.default })));
const ProjectPage = React.lazy(() => import("./containers/Projects/Project/ProjectPage").then(m => ({ default: m.default })));
const ProjectSettingsPage = React.lazy(() => import("./containers/Settings/ProjectSettings/ProjectSettingsPage").then(m => ({ default: m.default })));
const ProjectsPage = React.lazy(() => import("./containers/Projects/Projects/ProjectsPage").then(m => ({ default: m.default })));
const RolePage = React.lazy(() => import("./containers/Lookups/Role/RolePage").then(m => ({ default: m.default })));
const RolesPage = React.lazy(() => import("./containers/Lookups/Roles/RolesPage").then(m => ({ default: m.default })));
const SalutationPage = React.lazy(() => import("./containers/Lookups/Salutation/SalutationPage").then(m => ({ default: m.default })));
const SalutationsPage = React.lazy(() => import("./containers/Lookups/Salutations/SalutationsPage").then(m => ({ default: m.default })));
const SearchPage = React.lazy(() => import("./containers/Search/SearchPage").then(m => ({ default: m.default })));
const SettingsPage = React.lazy(() => import("./containers/Settings/SettingsPage").then(m => ({ default: m.default })));
const SiteLocationPage = React.lazy(() => import("./containers/InstitutionLookups/SiteLocation/SiteLocationPage").then(m => ({ default: m.default })));
const SiteLocationsPage = React.lazy(() => import("./containers/InstitutionLookups/SiteLocations/SiteLocationsPage").then(m => ({ default: m.default })));
const StatePage = React.lazy(() => import("./containers/Lookups/State/StatePage").then(m => ({ default: m.default })));
const StatesPage = React.lazy(() => import("./containers/Lookups/States/StatesPage").then(m => ({ default: m.default })));
const TrialAccrualsPage = React.lazy(() => import("./containers/Trials/TrialAccruals/TrialAccrualsPage").then(m => ({ default: m.default })));
const TrialDoiPage = React.lazy(() => import("./containers/Trials/TrialDoi/TrialDoiPage").then(m => ({ default: m.default })));
const TrialFinancePage = React.lazy(() => import("./containers/Trials/TrialFinance/TrialFinancePage").then(m => ({ default: m.default })));
const TrialGroupContractPage = React.lazy(() => import("./containers/TrialGroup/TrialGroupContract/TrialGroupContractPage").then(m => ({ default: m.default })));
const TrialGroupContractsPage = React.lazy(() => import("./containers/TrialGroup/TrialGroupContracts/TrialGroupContractsPage").then(m => ({ default: m.default })));
const TrialGroupFinancePage = React.lazy(() => import("./containers/TrialGroup/TrialGroupFinance/TrialGroupFinancePage").then(m => ({ default: m.default })));
const TrialGroupItemTemplateCategoriesPage = React.lazy(() => import("./containers/TrialGroupLookups/TrialGroupItemTemplateCategories/TrialGroupItemTemplateCategoriesPage").then(m => ({ default: m.default })));
const TrialGroupItemTemplateCategoryPage = React.lazy(() => import("./containers/TrialGroupLookups/TrialGroupItemTemplateCategory/TrialGroupItemTemplateCategoryPage").then(m => ({ default: m.default })));
const TrialGroupItemTemplatePage = React.lazy(() => import("./containers/TrialGroupLookups/TrialGroupItemTemplate/TrialGroupItemTemplatePage").then(m => ({ default: m.default })));
const TrialGroupItemTemplatesPage = React.lazy(() => import("./containers/TrialGroupLookups/TrialGroupItemTemplates/TrialGroupItemTemplatesPage").then(m => ({ default: m.default })));
const TrialGroupItemsPage = React.lazy(() => import("./containers/TrialGroup/TrialGroupItems/TrialGroupItemsPageSWR").then(m => ({ default: m.default })));
const TrialGroupLaboratoriesPage = React.lazy(() => import("./containers/TrialGroup/TrialGroupLaboratories/TrialGroupLaboratoriesPage").then(m => ({ default: m.default })));
const TrialGroupLaboratoryContractPage = React.lazy(() => import("./containers/TrialGroup/TrialGroupLaboratoryContract/TrialGroupLaboratoryContractPage").then(m => ({ default: m.default })));
const TrialGroupLaboratoryContractsPage = React.lazy(() => import("./containers/TrialGroup/TrialGroupLaboratoryContracts/TrialGroupLaboratoryContractsPage").then(m => ({ default: m.default })));
const TrialGroupLaboratoryFinancePage = React.lazy(() => import("./containers/TrialGroup/TrialGroupLaboratoryFinance/TrialGroupLaboratoryFinancePage").then(m => ({ default: m.default })));
const TrialGroupLaboratoryPage = React.lazy(() => import("./containers/TrialGroup/TrialGroupLaboratory/TrialGroupLaboratoryPage").then(m => ({ default: m.default })));
const TrialGroupLaboratoryPersonnelPage = React.lazy(() => import("./containers/TrialGroup/TrialGroupLaboratoryPersonnel/TrialGroupLaboratoryPersonnelPage").then(m => ({ default: m.default })));
const TrialGroupLaboratoryPersonnelsPage = React.lazy(() => import("./containers/TrialGroup/TrialGroupLaboratoryPersonnels/TrialGroupLaboratoryPersonnelsPage").then(m => ({ default: m.default })));
const TrialGroupPage = React.lazy(() => import("./containers/TrialGroup/TrialGroupPage/TrialGroupPageSWR").then(m => ({ default: m.default })));
const TrialGroupPersonnelPage = React.lazy(() => import("./containers/TrialGroup/TrialGroupPersonnel/TrialGroupPersonnelPage").then(m => ({ default: m.default })));
const TrialGroupPersonnelsPage = React.lazy(() => import("./containers/TrialGroup/TrialGroupPersonnels/TrialGroupPersonnelsPage").then(m => ({ default: m.default })));
const TrialGroupSettingsPage = React.lazy(() => import("./containers/Settings/TrialGroupSettings/TrialGroupSettingsPage").then(m => ({ default: m.default })));
const TrialGroupsPage = React.lazy(() => import("./containers/TrialGroup/TrialGroups/TrialGroupsPageSWR").then(m => ({ default: m.default })));
const TrialInstitutionContractPage = React.lazy(() => import("./containers/Trials/TrialInstitutionContract/TrialInstitutionContractPage").then(m => ({ default: m.default })));
const TrialInstitutionContractsPage = React.lazy(() => import("./containers/TrialInstitution/TrialInstitutionContracts/TrialInstitutionContractsPage").then(m => ({ default: m.default })));
const TrialInstitutionFinancePage = React.lazy(() => import("./containers/TrialInstitution/TrialInstitutionFinance/TrialInstitutionFinancePage").then(m => ({ default: m.default })));
const TrialInstitutionItemCustomViewDataEntryPage = React.lazy(() => import("./containers/Trials/TrialInstitutionItemCustomViewDataEntry/TrialInstitutionItemCustomViewDataEntryPage").then(m => ({ default: m.default })));
const TrialInstitutionItemCustomViewPage = React.lazy(() => import("./containers/Trials/TrialInstitutionItemCustomView/TrialInstitutionItemCustomViewPage").then(m => ({ default: m.default })));
const TrialInstitutionItemCustomViewsPage = React.lazy(() => import("./containers/Trials/TrialInstitutionItemCustomViews/TrialInstitutionItemCustomViewsPage").then(m => ({ default: m.default })));
const TrialInstitutionItemTemplateCategoriesPage = React.lazy(() => import("./containers/TrialInstitutionLookups/TrialInstitutionItemTemplateCategories/TrialInstitutionItemTemplateCategoriesPage").then(m => ({ default: m.default })));
const TrialInstitutionItemTemplateCategoryPage = React.lazy(() => import("./containers/TrialInstitutionLookups/TrialInstitutionItemTemplateCategory/TrialInstitutionItemTemplateCategoryPage").then(m => ({ default: m.default })));
const TrialInstitutionItemTemplatePage = React.lazy(() => import("./containers/TrialInstitutionLookups/TrialInstitutionItemTemplate/TrialInstitutionItemTemplatePage").then(m => ({ default: m.default })));
const TrialInstitutionItemTemplatesPage = React.lazy(() => import("./containers/TrialInstitutionLookups/TrialInstitutionItemTemplates/TrialInstitutionItemTemplatesPage").then(m => ({ default: m.default })));
const TrialInstitutionItemsPage = React.lazy(() => import("./containers/TrialInstitution/TrialInstitutionItems/TrialInstitutionItemsPageSWR").then(m => ({ default: m.default })));
const TrialInstitutionLaboratoriesPage = React.lazy(() => import("./containers/TrialInstitution/TrialInstitutionLaboratories/TrialInstitutionLaboratoriesPage").then(m => ({ default: m.default })));
const TrialInstitutionLaboratoryContractPage = React.lazy(() => import("./containers/TrialInstitution/TrialInstitutionLaboratoryContract/TrialInstitutionLaboratoryContractPage").then(m => ({ default: m.default })));
const TrialInstitutionLaboratoryContractsPage = React.lazy(() => import("./containers/TrialInstitution/TrialInstitutionLaboratoryContracts/TrialInstitutionLaboratoryContractsPage").then(m => ({ default: m.default })));
const TrialInstitutionLaboratoryFinancePage = React.lazy(() => import("./containers/TrialInstitution/TrialInstitutionLaboratoryFinance/TrialInstitutionLaboratoryFinancePage").then(m => ({ default: m.default })));
const TrialInstitutionLaboratoryPage = React.lazy(() => import("./containers/TrialInstitution/TrialInstitutionLaboratory/TrialInstitutionLaboratoryPage").then(m => ({ default: m.default })));
const TrialInstitutionLaboratoryPersonnelPage = React.lazy(() => import("./containers/TrialInstitution/TrialInstitutionLaboratoryPersonnel/TrialInstitutionLaboratoryPersonnelPage").then(m => ({ default: m.default })));
const TrialInstitutionLaboratoryPersonnelsPage = React.lazy(() => import("./containers/TrialInstitution/TrialInstitutionLaboratoryPersonnels/TrialInstitutionLaboratoryPersonnelsPage").then(m => ({ default: m.default })));
const TrialInstitutionNotesPage = React.lazy(() => import("./containers/TrialInstitution/TrialInstitutionNotes/TrialInstitutionNotesPage").then(m => ({ default: m.default })));
const TrialInstitutionPage = React.lazy(() => import("./containers/TrialInstitution/TrialInstitution/TrialInstitutionPageSWR").then(m => ({ default: m.default })));
const TrialInstitutionPersonnelPage = React.lazy(() => import("./containers/TrialInstitution/TrialInstitutionPersonnel/TrialInstitutionPersonnelPage").then(m => ({ default: m.default })));
const TrialInstitutionPersonnelsPage = React.lazy(() => import("./containers/TrialInstitution/TrialInstitutionPersonnels/TrialInstitutionPersonnelsPage").then(m => ({ default: m.default })));
const TrialInstitutionSettingsPage = React.lazy(() => import("./containers/Settings/TrialInstitutionSettings/TrialInstitutionSettingsPage").then(m => ({ default: m.default })));
const TrialInstitutionsPage = React.lazy(() => import("./containers/Trials/TrialInstitutions/TrialInstitutionsPage").then(m => ({ default: m.default })));
const TrialItemTemplateCategoriesPage = React.lazy(() => import("./containers/TrialLookups/TrialItemTemplateCategories/TrialItemTemplateCategoriesPage").then(m => ({ default: m.default })));
const TrialItemTemplateCategoryPage = React.lazy(() => import("./containers/TrialLookups/TrialItemTemplateCategory/TrialItemTemplateCategoryPage").then(m => ({ default: m.default })));
const TrialItemTemplatePage = React.lazy(() => import("./containers/TrialLookups/TrialItemTemplate/TrialItemTemplatePage").then(m => ({ default: m.default })));
const TrialItemTemplatesPage = React.lazy(() => import("./containers/TrialLookups/TrialItemTemplates/TrialItemTemplatesPage").then(m => ({ default: m.default })));
const TrialItemsPage = React.lazy(() => import("./containers/Trials/TrialItems/TrialItemsPageSWR").then(m => ({ default: m.default })));
const TrialLaboratoriesPage = React.lazy(() => import("./containers/Trials/TrialLaboratories/TrialLaboratoriesPage").then(m => ({ default: m.default })));
const TrialNotesPage = React.lazy(() => import("./containers/Trials/TrialNotes/TrialNotesPage").then(m => ({ default: m.default })));
const TrialPage = React.lazy(() => import("./containers/Trials/Trial/TrialPage").then(m => ({ default: m.default })));
const TrialPersonnelPage = React.lazy(() => import("./containers/Trials/TrialPersonnel/TrialPersonnelPage").then(m => ({ default: m.default })));
const TrialPersonnelsPage = React.lazy(() => import("./containers/Trials/TrialPersonnels/TrialPersonnelsPage").then(m => ({ default: m.default })));
const TrialSettingsPage = React.lazy(() => import("./containers/Settings/TrialSettings/TrialSettingsPage").then(m => ({ default: m.default })));
const TrialTypePage = React.lazy(() => import("./containers/TrialLookups/TrialType/TrialTypePage").then(m => ({ default: m.default })));
const TrialTypesPage = React.lazy(() => import("./containers/TrialLookups/TrialTypes/TrialTypesPage").then(m => ({ default: m.default })));
const TrialWebsitePage = React.lazy(() => import("./containers/Trials/TrialWebsite/TrialWebsitePage").then(m => ({ default: m.default })));
const TrialsPage = React.lazy(() => import("./containers/Trials/TrialsPage/TrialsPage").then(m => ({ default: m.default })));

export const routes: AppRouteObject[] = [
    {
        path: "/",
        element: <AppLayout />,
        hydrateFallbackElement: <OverlayLoading />,
        errorElement: <RouteErrorBoundary />,
        handle: { help: null },
        children: [
            {
                index: true,
                element: <Navigate replace to="/dashboard" />,
                handle: { help: null }
            },
            {
                path: "*",
                element: <Navigate replace to="/error/404" />,
                handle: { help: null }
            },
            {
                path: "dashboard",
                element: (
                    <React.Suspense fallback={<OverlayLoading />}>
                        <DashboardPage />
                    </React.Suspense>
                ),
                handle: { help: { topicKey: "dashboard" } }
            },
            {
                path: "search",
                element: (
                    <React.Suspense fallback={<OverlayLoading />}>
                        <SearchPage />
                    </React.Suspense>
                ),
                handle: { help: { topicKey: "search" } }
            },
            {
                path: "mytrials",
                element: (
                    <React.Suspense fallback={<OverlayLoading />}>
                        <MyTrialsPage />
                    </React.Suspense>
                ),
                handle: { help: { topicKey: "my-trials" } }
            },
            {
                path: "error",
                handle: { help: null },
                children: [
                    {
                        path: ":errorCode",
                        element: (
                            <React.Suspense fallback={<OverlayLoading />}>
                                <ErrorPage />
                            </React.Suspense>
                        ),
                        handle: { help: { topicKey: "error" } }
                    },
                ],
            },
            {
                path: "settings",
                handle: { help: null },
                children: [
                    {
                        index: true,
                        element: (
                            <React.Suspense fallback={<OverlayLoading />}>
                                <SettingsPage />
                            </React.Suspense>
                        ),
                        handle: { help: { topicKey: "settings" } }
                    },
                    {
                        path: "general",
                        handle: { help: null },
                        children: [
                            {
                                index: true,
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <GeneralSettingsPage />
                                    </React.Suspense>
                                ),
                                handle: { help: { topicKey: "general-settings" } }
                            },
                            {
                                path: "countries",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <CountriesPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "countries" } }
                                    },
                                    {
                                        path: ":countryId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <CountryPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "country" } }
                                    },
                                ],
                            },
                            {
                                path: "roles",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <RolesPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "roles" } }
                                    },
                                    {
                                        path: ":roleId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <RolePage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "role" } }
                                    },
                                ],
                            },
                            {
                                path: "states",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <StatesPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "states" } }
                                    },
                                    {
                                        path: ":stateId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <StatePage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "state" } }
                                    },
                                ],
                            },
                            {
                                path: "itemcomponenttypes",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentTypesPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "item-component-types" } }
                                    },
                                    {
                                        path: ":itemcomponenttypesId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentTypePage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "item-component-type" } }
                                    },
                                ],
                            },
                            {
                                path: "notetypes",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <NoteTypesPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "note-types" } }
                                    },
                                    {
                                        path: ":notetypesId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <NoteTypePage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "note-type" } }
                                    },
                                ],
                            },
                            {
                                path: "interests",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <InterestsPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "interests" } }
                                    },
                                    {
                                        path: ":interestId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <InterestPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "interest" } }
                                    },
                                ],
                            },
                            {
                                path: "membershiptypes",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <MembershipTypesPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "membership-types" } }
                                    },
                                    {
                                        path: ":membershiptypesId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <MembershipTypePage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "membership-type" } }
                                    },
                                ],
                            },
                            {
                                path: "professionaldisciplines",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <ProfessionalDisciplinesPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "professional-disciplines" } }
                                    },
                                    {
                                        path: ":professionaldisciplinesId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ProfessionalDisciplinePage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "professional-discipline" } }
                                    },
                                ],
                            },
                            {
                                path: "salutations",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <SalutationsPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "salutations" } }
                                    },
                                    {
                                        path: ":salutationId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <SalutationPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "salutation" } }
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: "project",
                        handle: { help: null },
                        children: [
                            {
                                index: true,
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <ProjectSettingsPage />
                                    </React.Suspense>
                                ),
                                handle: { help: { topicKey: "project-settings" } }
                            },
                            {
                                path: "itemtemplates",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ProjectItemTemplatesPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "project-item-templates" } }
                                    },
                                    {
                                        path: ":projectItemTemplateId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ProjectItemTemplatePage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "project-item-template" } }
                                    },
                                ],
                            },
                            {
                                path: "itemcomponents",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentsPage context="project" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "item-components" } }
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentPage context="project" mode="create" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "item-component" } },
                                    },
                                    {
                                        path: "create/:cloneId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentPage context="project" mode="clone" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "item-component" } },
                                    },
                                    {
                                        path: ":itemComponentId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentPage context="project" mode="edit" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "item-component" } },
                                    },
                                ],
                            },
                            {
                                path: "itemCategories",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <ProjectItemTemplateCategorysPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "project-item-template-categorys" } }
                                    },
                                    {
                                        path: ":projectItemTemplateCategoryId",
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <ProjectItemTemplateCategoryPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "project-item-template-category" } }
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: "trial",
                        handle: { help: null },
                        children: [
                            {
                                index: true,
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <TrialSettingsPage />
                                    </React.Suspense>
                                ),
                                handle: { help: { topicKey: "trial-settings" } }
                            },
                            {
                                path: "itemtemplates",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <TrialItemTemplatesPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "trial-item-templates" } }
                                    },
                                    {
                                        path: ":trialId",
                                        handle: { help: null },
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <TrialItemTemplatesPage />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "trial-item-templates" } }
                                            },
                                            {
                                                path: "template",
                                                handle: { help: null },
                                                children: [
                                                    {
                                                        path: ":trialItemTemplateId",
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <TrialItemTemplatePage />
                                                            </React.Suspense>
                                                        ),
                                                        handle: { help: { topicKey: "trial-item-template" } }
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                    {
                                        path: "template",
                                        handle: { help: null },
                                        children: [
                                            {
                                                path: ":trialItemTemplateId",
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <TrialItemTemplatePage />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "trial-item-template" } }
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "itemcomponents",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentsPage context="trial" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "item-components" } }
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentPage context="trial" mode="create" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "item-component" } },
                                    },
                                    {
                                        path: "create/:cloneId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentPage context="trial" mode="clone" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "item-component" } },
                                    },
                                    {
                                        path: ":itemComponentId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentPage context="trial" mode="edit" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "item-component" } },
                                    },
                                ],
                            },
                            {
                                path: "itemtemplatecategories",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <TrialItemTemplateCategoriesPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "trial-item-template-categories" } }
                                    },
                                    {
                                        path: ":trialItemTemplateCategoryId",
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <TrialItemTemplateCategoryPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "trial-item-template-category" } }
                                    },
                                ],
                            },
                            {
                                path: "trialtypes",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <TrialTypesPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "trial-types" } }
                                    },
                                    {
                                        path: ":trialTypeId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <TrialTypePage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "trial-type" } }
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: "trialinstitution",
                        handle: { help: null },
                        children: [
                            {
                                index: true,
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <TrialInstitutionSettingsPage />
                                    </React.Suspense>
                                ),
                                handle: { help: { topicKey: "trial-institution-settings" } }
                            },
                            {
                                path: "itemtemplates",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <TrialInstitutionItemTemplatesPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "trial-institution-item-templates" } }
                                    },
                                    {
                                        path: ":trialId",
                                        handle: { help: null },
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <TrialInstitutionItemTemplatesPage />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "trial-institution-item-templates" } }
                                            },
                                            {
                                                path: "template",
                                                handle: { help: null },
                                                children: [
                                                    {
                                                        path: ":trialInstitutionItemTemplateId",
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <TrialInstitutionItemTemplatePage />
                                                            </React.Suspense>
                                                        ),
                                                        handle: { help: { topicKey: "trial-institution-item-template" } }
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                    {
                                        path: "template",
                                        handle: { help: null },
                                        children: [
                                            {
                                                path: ":trialInstitutionItemTemplateId",
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <TrialInstitutionItemTemplatePage />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "trial-institution-item-template" } }
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "itemcomponents",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentsPage context="trialInstitution" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "item-components" } }
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentPage context="trialInstitution" mode="create" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "item-component" } },
                                    },
                                    {
                                        path: "create/:cloneId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentPage context="trialInstitution" mode="clone" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "item-component" } },
                                    },
                                    {
                                        path: ":itemComponentId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentPage context="trialInstitution" mode="edit" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "item-component" } },
                                    },
                                ],
                            },
                            {
                                path: "itemtemplatecategories",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <TrialInstitutionItemTemplateCategoriesPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "trial-institution-item-template-categories" } }
                                    },
                                    {
                                        path: ":trialInstitutionItemTemplateCategoryId",
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <TrialInstitutionItemTemplateCategoryPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "trial-institution-item-template-category" } }
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: "trialgroup",
                        handle: { help: null },
                        children: [
                            {
                                index: true,
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <TrialGroupSettingsPage />
                                    </React.Suspense>
                                ),
                                handle: { help: { topicKey: "trial-group-settings" } }
                            },
                            {
                                path: "itemtemplates",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <TrialGroupItemTemplatesPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "trial-group-item-templates" } }
                                    },
                                    {
                                        path: ":groupId",
                                        handle: { help: null },
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <TrialGroupItemTemplatesPage />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "trial-group-item-templates" } }
                                            },
                                            {
                                                path: "template",
                                                handle: { help: null },
                                                children: [
                                                    {
                                                        path: ":trialGroupItemTemplateId",
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <TrialGroupItemTemplatePage />
                                                            </React.Suspense>
                                                        ),
                                                        handle: { help: { topicKey: "trial-group-item-template" } }
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                    {
                                        path: "template",
                                        handle: { help: null },
                                        children: [
                                            {
                                                path: ":trialGroupItemTemplateId",
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <TrialGroupItemTemplatePage />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "trial-group-item-template" } }
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "itemcomponents",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentsPage context="trialGroup" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "item-components" } }
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentPage context="trialGroup" mode="create" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "item-component" } },
                                    },
                                    {
                                        path: "create/:cloneId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentPage context="trialGroup" mode="clone" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "item-component" } },
                                    },
                                    {
                                        path: ":itemComponentId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentPage context="trialGroup" mode="edit" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "item-component" } },
                                    },
                                ],
                            },
                            {
                                path: "itemtemplatecategories",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <TrialGroupItemTemplateCategoriesPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "trial-group-item-template-categories" } }
                                    },
                                    {
                                        path: ":trialGroupItemTemplateCategoryId",
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <TrialGroupItemTemplateCategoryPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "trial-group-item-template-category" } }
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: "group",
                        handle: { help: null },
                        children: [
                            {
                                index: true,
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <GroupSettingsPage />
                                    </React.Suspense>
                                ),
                                handle: { help: { topicKey: "group-settings" } },
                            },
                            {
                                path: "itemtemplates",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <GroupItemTemplatesPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "group-item-templates" } }
                                    },
                                    {
                                        path: ":groupId",
                                        handle: { help: null },
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <GroupItemTemplatesPage />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "group-item-templates" } }
                                            },
                                            {
                                                path: "template",
                                                handle: { help: null },
                                                children: [
                                                    {
                                                        path: ":groupItemTemplateId",
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <GroupItemTemplatePage />
                                                            </React.Suspense>
                                                        ),
                                                        handle: { help: { topicKey: "group-item-template" } }
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                    {
                                        path: "template",
                                        handle: { help: null },
                                        children: [
                                            {
                                                path: ":groupItemTemplateId",
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <GroupItemTemplatePage />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "group-item-template" } }
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "itemcomponents",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentsPage context="group" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "item-components" } }
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentPage context="group" mode="create" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "item-component" } },
                                    },
                                    {
                                        path: "create/:cloneId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentPage context="group" mode="clone" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "item-component" } },
                                    },
                                    {
                                        path: ":itemComponentId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentPage context="group" mode="edit" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "item-component" } },
                                    },
                                ],
                            },
                            {
                                path: "itemCategories",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <GroupItemTemplateCategorysPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "group-item-template-categorys" } }
                                    },
                                    {
                                        path: ":groupItemTemplateCategoryId",
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <GroupItemTemplateCategoryPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "group-item-template-category" } }
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: "institution",
                        handle: { help: null },
                        children: [
                            {
                                index: true,
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <InstitutionSettingsPage />
                                    </React.Suspense>
                                ),
                                handle: { help: { topicKey: "institution-settings" } }
                            },
                            {
                                path: "itemtemplates",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <InstitutionItemTemplatesPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "institution-item-templates" } }
                                    },
                                    {
                                        path: ":institutionId",
                                        handle: { help: null },
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <InstitutionItemTemplatesPage />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "institution-item-templates" } }
                                            },
                                            {
                                                path: "template",
                                                handle: { help: null },
                                                children: [
                                                    {
                                                        path: ":institutionItemTemplateId",
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <InstitutionItemTemplatePage />
                                                            </React.Suspense>
                                                        ),
                                                        handle: { help: { topicKey: "institution-item-template" } }
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                    {
                                        path: "template",
                                        handle: { help: null },
                                        children: [
                                            {
                                                path: ":institutionItemTemplateId",
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <InstitutionItemTemplatePage />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "institution-item-template" } }
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "itemcomponents",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentsPage context="institution" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "item-components" } }
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentPage context="institution" mode="create" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "item-component" } },
                                    },
                                    {
                                        path: "create/:cloneId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentPage context="institution" mode="clone" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "item-component" } },
                                    },
                                    {
                                        path: ":itemComponentId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentPage context="institution" mode="edit" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "item-component" } },
                                    },
                                ],
                            },
                            {
                                path: "itemtemplatecategories",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <InstitutionItemTemplateCategoriesPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "institution-item-template-categories" } }
                                    },
                                    {
                                        path: ":institutionItemTemplateCategoryId",
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <InstitutionItemTemplateCategoryPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "institution-item-template-category" } }
                                    },
                                ],
                            },
                            {
                                path: "sitelocations",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <SiteLocationsPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "site-locations" } }
                                    },
                                    {
                                        path: ":siteLocationId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <SiteLocationPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "site-location" } }
                                    },
                                ],
                            },
                            {
                                path: "keycontacts",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <KeyContactsPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "key-contacts" } }
                                    },
                                    {
                                        path: ":keyContactId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <KeyContactPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "key-contact" } }
                                    },
                                ],
                            },
                            {
                                path: "institutiontypes",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <InstitutionTypesPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "institution-types" } }
                                    },
                                    {
                                        path: ":institutionTypeId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <InstitutionTypePage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "institution-type" } }
                                    },
                                ],
                            },
                            {
                                path: "institutioncodings",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <InstitutionCodingsPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "institution-codings" } }
                                    },
                                    {
                                        path: ":institutionCodingId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <InstitutionCodingPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "institution-coding" } }
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: "hrecs",
                        handle: { help: null },
                        children: [
                            {
                                index: true,
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <HrecsPage />
                                    </React.Suspense>
                                ),
                                handle: { help: { topicKey: "hrecs" } }
                            },
                            {
                                path: "create",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <HrecPage />
                                    </React.Suspense>
                                ),
                                handle: { help: { topicKey: "hrec" } }
                            },
                            {
                                path: ":hrecId",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <Navigate replace to="details" />
                                        ),
                                        handle: { help: null }
                                    },
                                    {
                                        path: "details",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <HrecPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "hrec" } }
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: "healthdistricts",
                        handle: { help: null },
                        children: [
                            {
                                index: true,
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <HealthDistrictsPage />
                                    </React.Suspense>
                                ),
                                handle: { help: { topicKey: "health-districts" } }
                            },
                            {
                                path: "create",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <HealthDistrictPage />
                                    </React.Suspense>
                                ),
                                handle: { help: { topicKey: "health-district" } }
                            },
                            {
                                path: ":healthDistrictId",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <Navigate replace to="details" />
                                        ),
                                        handle: { help: null }
                                    },
                                    {
                                        path: "details",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <HealthDistrictPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "health-district" } }
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                path: "trials",
                handle: { help: null },
                children: [
                    {
                        index: true,
                        element: (
                            <React.Suspense fallback={<OverlayLoading />}>
                                <TrialsPage />
                            </React.Suspense>
                        ),
                        handle: { help: { topicKey: "trials" } }
                    },
                    {
                        path: "create",
                        element: (
                            <React.Suspense fallback={<OverlayLoading />}>
                                <TrialPage mode="create" />
                            </React.Suspense>
                        ),
                        handle: { help: { topicKey: "trial" } }
                    },
                    {
                        path: ":trialId",
                        handle: { help: null },
                        children: [
                            {
                                index: true,
                                element: <Navigate replace to="details" />,
                                handle: { help: null }
                            },
                            {
                                path: "details",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <TrialPage mode="edit" />
                                    </React.Suspense>
                                ),
                                handle: { help: { topicKey: "trial" } }
                            },
                            {
                                path: "institutions",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <TrialInstitutionsPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "trial-institutions" } }
                                    },
                                ],
                            },
                            {
                                path: "trialinstitution",
                                handle: { help: null },
                                children: [
                                    {
                                        path: "create/details",
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <TrialInstitutionPage mode="create" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "trial-institution" } }
                                    },
                                    {
                                        path: ":trialInstitutionId",
                                        handle: { help: null },
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                handle: { help: null }
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <TrialInstitutionPage mode="edit" />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "trial-institution" } }
                                            },
                                            {
                                                path: "items",
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <TrialInstitutionItemsPage />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "trial-institution-items" } }
                                            },
                                            {
                                                path: "personnel",
                                                handle: { help: null },
                                                children: [
                                                    {
                                                        index: true,
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <TrialInstitutionPersonnelsPage />
                                                            </React.Suspense>
                                                        ),
                                                        handle: { help: { topicKey: "trial-institution-personnels" } }
                                                    },
                                                    {
                                                        path: "create",
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <TrialInstitutionPersonnelPage />
                                                            </React.Suspense>
                                                        ),
                                                        handle: { help: { topicKey: "trial-institution-personnel" } }
                                                    },
                                                    {
                                                        path: ":personnelId",
                                                        handle: { help: null },
                                                        children: [
                                                            {
                                                                index: true,
                                                                element: (
                                                                    <Navigate replace to="details" />
                                                                ),
                                                                handle: { help: null }
                                                            },
                                                            {
                                                                path: "details",
                                                                element: (
                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                        <TrialInstitutionPersonnelPage />
                                                                    </React.Suspense>
                                                                ),
                                                                handle: { help: { topicKey: "trial-institution-personnel" } }
                                                            },
                                                        ],
                                                    },
                                                ],
                                            },
                                            {
                                                path: "notes",
                                                handle: { help: null },
                                                children: [
                                                    {
                                                        index: true,
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <TrialInstitutionNotesPage />
                                                            </React.Suspense>
                                                        ),
                                                        handle: { help: { topicKey: "trial-institution-notes" } }
                                                    },
                                                    {
                                                        path: "create",
                                                        element: (
                                                            <React.Suspense fallback={<OverlayLoading />}>
                                                                <NotePage kind="trialInstitution" mode="create" />
                                                            </React.Suspense>
                                                        ),
                                                        handle: { help: { topicKey: "note" } }
                                                    },
                                                    {
                                                        path: ":noteId",
                                                        handle: { help: null },
                                                        children: [
                                                            {
                                                                index: true,
                                                                element: (
                                                                    <Navigate replace to="details" />
                                                                ),
                                                                handle: { help: null }
                                                            },
                                                            {
                                                                path: "details",
                                                                element: (
                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                        <NotePage kind="trialInstitution" mode="edit" />
                                                                    </React.Suspense>
                                                                ),
                                                                handle: { help: { topicKey: "note" } }
                                                            },
                                                        ],
                                                    },
                                                ],
                                            },
                                            {
                                                path: "labs",
                                                handle: { help: null },
                                                children: [
                                                    {
                                                        index: true,
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <TrialInstitutionLaboratoriesPage />
                                                            </React.Suspense>
                                                        ),
                                                        handle: { help: { topicKey: "trial-institution-laboratories" } }
                                                    },
                                                    {
                                                        path: "create",
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <TrialInstitutionLaboratoryPage
                                                                    kind="trialInstitution"
                                                                    mode="create"
                                                                />
                                                            </React.Suspense>
                                                        ),
                                                        handle: { help: { topicKey: "trial-institution-laboratory" } }
                                                    },
                                                    {
                                                        path: ":trialInstitutionLaboratoryId",
                                                        handle: { help: null },
                                                        children: [
                                                            {
                                                                index: true,
                                                                element: (
                                                                    <Navigate replace to="details" />
                                                                ),
                                                                handle: { help: null }
                                                            },
                                                            {
                                                                path: "details",
                                                                element: (
                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                        <TrialInstitutionLaboratoryPage
                                                                            kind="trialInstitution"
                                                                            mode="edit"
                                                                        />
                                                                    </React.Suspense>
                                                                ),
                                                                handle: { help: { topicKey: "trial-institution-laboratory" } }
                                                            },
                                                            {
                                                                path: "personnel",
                                                                handle: { help: null },
                                                                children: [
                                                                    {
                                                                        index: true,
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <TrialInstitutionLaboratoryPersonnelsPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                            handle: { help: { topicKey: "trial-institution-laboratory-personnels" } }
                                                                    },
                                                                    {
                                                                        path: "create",
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <TrialInstitutionLaboratoryPersonnelPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                            handle: { help: { topicKey: "trial-institution-laboratory-personnel" } }
                                                                    },
                                                                    {
                                                                        path: ":trialInstitutionLaboratoryPersonnelId",
                                                                        handle: { help: null },
                                                                        children:
                                                                            [
                                                                                {
                                                                                    index: true,
                                                                                    element:
                                                                                        (
                                                                                            <Navigate replace to="details" />
                                                                                        ),
                                                                                        handle: { help: null }
                                                                                },
                                                                                {
                                                                                    path: "details",
                                                                                    element: (
                                                                                        <React.Suspense fallback={<div>Loading...</div>}>
                                                                                            <TrialInstitutionLaboratoryPersonnelPage />
                                                                                        </React.Suspense>
                                                                                    ),
                                                                                        handle: { help: { topicKey: "trial-institution-laboratory-personnel" } }
                                                                                },
                                                                            ],
                                                                    },
                                                                ],
                                                            },
                                                            {
                                                                path: "finance",
                                                                element: (
                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                        <TrialInstitutionLaboratoryFinancePage />
                                                                    </React.Suspense>
                                                                ),
                                                                handle: { help: { topicKey: "trial-institution-laboratory-finance" } }
                                                            },
                                                            {
                                                                path: "contracts",
                                                                handle: { help: null },
                                                                children: [
                                                                    {
                                                                        index: true,
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <TrialInstitutionLaboratoryContractsPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                            handle: { help: { topicKey: "trial-institution-laboratory-contracts" } }
                                                                    },
                                                                    {
                                                                        path: "create",
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <TrialInstitutionLaboratoryContractPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                            handle: { help: { topicKey: "trial-institution-laboratory-contract" } }
                                                                    },
                                                                    {
                                                                        path: ":trialInstitutionLaboratoryContractId",
                                                                        handle: { help: null },
                                                                        children:
                                                                            [
                                                                                {
                                                                                    index: true,
                                                                                    element:
                                                                                        (
                                                                                            <Navigate replace to="details" />
                                                                                        ),
                                                                                        handle: { help: null }
                                                                                },
                                                                                {
                                                                                    path: "details",
                                                                                    element: (
                                                                                        <React.Suspense fallback={<div>Loading...</div>}>
                                                                                            <TrialInstitutionLaboratoryContractPage />
                                                                                        </React.Suspense>
                                                                                    ),
                                                                                        handle: { help: { topicKey: "trial-institution-laboratory-contract" } }
                                                                                },
                                                                            ],
                                                                    },
                                                                ],
                                                            },
                                                        ],
                                                    },
                                                ],
                                            },
                                            {
                                                path: "finance",
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <TrialInstitutionFinancePage />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "trial-institution-finance" } }
                                            },
                                            {
                                                path: "contracts",
                                                handle: { help: null },
                                                children: [
                                                    {
                                                        index: true,
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <TrialInstitutionContractsPage />
                                                            </React.Suspense>
                                                        ),
                                                        handle: { help: { topicKey: "trial-institution-contracts" } }
                                                    },
                                                    {
                                                        path: "create",
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <TrialInstitutionContractPage />
                                                            </React.Suspense>
                                                        ),
                                                        handle: { help: { topicKey: "trial-institution-contract" } }
                                                    },
                                                    {
                                                        path: ":trialInstitutionContractId",
                                                        handle: { help: null },
                                                        children: [
                                                            {
                                                                index: true,
                                                                element: (
                                                                    <Navigate replace to="details" />
                                                                ),
                                                                handle: { help: null }
                                                            },
                                                            {
                                                                path: "details",
                                                                element: (
                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                        <TrialInstitutionContractPage />
                                                                    </React.Suspense>
                                                                ),
                                                                handle: { help: { topicKey: "trial-institution-contract" } }
                                                            },
                                                        ],
                                                    },
                                                ],
                                            },
                                            {
                                                path: "monitoring",
                                                handle: { help: null },
                                                children: [
                                                    {
                                                        index: true,
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <MonitoringsPage />
                                                            </React.Suspense>
                                                        ),
                                                        handle: { help: { topicKey: "monitorings" } }
                                                    },
                                                    {
                                                        path: "create",
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <MonitoringPage />
                                                            </React.Suspense>
                                                        ),
                                                        handle: { help: { topicKey: "monitoring" } }
                                                    },
                                                    {
                                                        path: ":monitoringId",
                                                        handle: { help: null },
                                                        children: [
                                                            {
                                                                index: true,
                                                                element: (
                                                                    <Navigate replace to="details" />
                                                                ),
                                                                handle: { help: null }
                                                            },
                                                            {
                                                                path: "details",
                                                                element: (
                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                        <MonitoringPage />
                                                                    </React.Suspense>
                                                                ),
                                                                handle: { help: { topicKey: "monitoring" } }
                                                            },
                                                            {
                                                                path: "documents",
                                                                handle: { help: null },
                                                                children: [
                                                                    {
                                                                        index: true,
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <MonitoringDocumentsPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                        handle: { help: { topicKey: "monitoring-documents" } }
                                                                    },
                                                                    {
                                                                        path: "create",
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <MonitoringDocumentPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                        handle: { help: { topicKey: "monitoring-document" } }
                                                                    },
                                                                    {
                                                                        path: ":monitoringDocumentId",
                                                                        handle: { help: null },
                                                                        children: [
                                                                            {
                                                                                index: true,
                                                                                element: (
                                                                                    <Navigate replace to="details" />
                                                                                ),
                                                                                handle: { help: null }
                                                                            },
                                                                            {
                                                                                path: "details",
                                                                                element: (
                                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                                        <MonitoringDocumentPage />
                                                                                    </React.Suspense>
                                                                                ),
                                                                                handle: { help: { topicKey: "monitoring-document" } }
                                                                            }
                                                                        ]
                                                                    }
                                                                ]
                                                            },
                                                            {
                                                                path: "issues",
                                                                handle: { help: { topicKey: "monitoring-issues" } },
                                                                children: [
                                                                    {
                                                                        index: true,
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <MonitoringIssuesPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                        handle: { help: { topicKey: "monitoring-issues" } }
                                                                    },
                                                                    {
                                                                        path: "create",
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <MonitoringIssuePage />
                                                                            </React.Suspense>
                                                                        ),
                                                                        handle: { help: { topicKey: "monitoring-issue" } }
                                                                    },
                                                                    {
                                                                        path: ":monitoringIssueId",
                                                                        handle: { help: null },
                                                                        children: [
                                                                            {
                                                                                index: true,
                                                                                element: (
                                                                                    <Navigate replace to="details" />
                                                                                ),
                                                                                handle: { help: null }
                                                                            },
                                                                            {
                                                                                path: "details",
                                                                                element: (
                                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                                        <MonitoringIssuePage />
                                                                                    </React.Suspense>
                                                                                ),
                                                                                handle: { help: { topicKey: "monitoring-issue" } }
                                                                            },
                                                                            {
                                                                                path: "documents",
                                                                                handle: { help: null },
                                                                                children: [
                                                                                    {
                                                                                        index: true,
                                                                                        element: (
                                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                                <MonitoringIssueDocumentsPage />
                                                                                            </React.Suspense>
                                                                                        ),
                                                                                        handle: { help: { topicKey: "monitoring-issue-documents" } }
                                                                                    },
                                                                                    {
                                                                                        path: "create",
                                                                                        element: (
                                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                                <MonitoringIssueDocumentPage />
                                                                                            </React.Suspense>
                                                                                        ),
                                                                                        handle: { help: { topicKey: "monitoring-issue-document" } }
                                                                                    },
                                                                                    {
                                                                                        path: ":monitoringIssueDocumentId",
                                                                                        handle: { help: null },
                                                                                        children: [
                                                                                            {
                                                                                                index: true,
                                                                                                element: (
                                                                                                    <Navigate replace to="details" />
                                                                                                ),
                                                                                                handle: { help: null }
                                                                                            },
                                                                                            {
                                                                                                path: "details",
                                                                                                element: (
                                                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                                                        <MonitoringIssueDocumentPage />
                                                                                                    </React.Suspense>
                                                                                                ),
                                                                                                handle: { help: { topicKey: "monitoring-issue-document" } }
                                                                                            }
                                                                                        ]
                                                                                    }
                                                                                ]
                                                                            },
                                                                        ]
                                                                    }
                                                                ]
                                                            }
                                                        ],
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "trialinstitutionitemviews",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <TrialInstitutionItemCustomViewsPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "trial-institution-item-custom-views" } }
                                    },
                                    {
                                        path: ":trialInstitutionItemCustomViewId/dataentry",
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <TrialInstitutionItemCustomViewDataEntryPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "trial-institution-item-custom-view-data-entry" } }
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <TrialInstitutionItemCustomViewPage mode="create" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "trial-institution-item-custom-view" } }
                                    },
                                    {
                                        path: ":trialInstitutionItemCustomViewId",
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <TrialInstitutionItemCustomViewPage mode="edit" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "trial-institution-item-custom-view" } }
                                    },
                                ],
                            },
                            {
                                path: "trialitems",
                                handle: { help: { topicKey: "trial-items" } },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <TrialItemsPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "trial-items" } }
                                    }
                                ],
                            },
                            {
                                path: "personnel",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <TrialPersonnelsPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "trial-personnel" } }
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <TrialPersonnelPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "trial-personnel" } }
                                    },
                                    {
                                        path: ":trialPersonnelId",
                                        handle: { help: null },
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                handle: { help: null }
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <TrialPersonnelPage />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "trial-personnel" } }
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "accrual",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <TrialAccrualsPage />
                                    </React.Suspense>
                                ),
                                handle: { help: { topicKey: "trial-accruals" } }
                            },
                            {
                                path: "notes",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <TrialNotesPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "trial-notes" } }
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <NotePage kind="trial" mode="create" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "note" } }
                                    },
                                    {
                                        path: ":noteId",
                                        handle: { help: null },
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                handle: { help: null }
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <NotePage kind="trial" mode="edit" />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "note" } }
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "groups",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <TrialGroupsPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "trial-groups" } }
                                    },
                                    {
                                        path: ":groupId",
                                        handle: { help: null },
                                        children: [
                                            {
                                                path: "trialgroup",
                                                handle: { help: null },
                                                children: [
                                                    {
                                                        path: ":trialGroupId",
                                                        handle: { help: { topicKey: "dashboard" } },
                                                        children: [
                                                            {
                                                                index: true,
                                                                element: (
                                                                    <Navigate replace to="details" />
                                                                ),
                                                                handle: { help: null }
                                                            },
                                                            {
                                                                path: "details",
                                                                element: (
                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                        <TrialGroupPage />
                                                                    </React.Suspense>
                                                                ),
                                                                handle: { help: { topicKey: "trial-group" } }
                                                            },
                                                            {
                                                                path: "personnel",
                                                                handle: { help: null },
                                                                children: [
                                                                    {
                                                                        index: true,
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <TrialGroupPersonnelsPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                            handle: { help: { topicKey: "trial-group-personnels" } }
                                                                    },
                                                                    {
                                                                        path: "create",
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <TrialGroupPersonnelPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                        handle: { help: { topicKey: "trial-group-personnel" } }
                                                                    },
                                                                    {
                                                                        path: ":trialPersonnelId",
                                                                        handle: { help: null },
                                                                        children:
                                                                            [
                                                                                {
                                                                                    index: true,
                                                                                    element:
                                                                                        (
                                                                                            <Navigate replace to="details" />
                                                                                        ),
                                                                                        handle: { help: null }
                                                                                },
                                                                                {
                                                                                    path: "details",
                                                                                    element: (
                                                                                        <React.Suspense fallback={<div>Loading...</div>}>
                                                                                            <TrialGroupPersonnelPage />
                                                                                        </React.Suspense>
                                                                                    ),
                                                                                    handle: { help: { topicKey: "trial-group-personnel" } }
                                                                                },
                                                                            ],
                                                                    },
                                                                ],
                                                            },
                                                            {
                                                                path: "items",
                                                                element: (
                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                        <TrialGroupItemsPage />
                                                                    </React.Suspense>
                                                                ),
                                                                handle: { help: { topicKey: "trial-group-items" } }
                                                            },
                                                            {
                                                                path: "labs",
                                                                handle: { help: null },
                                                                children: [
                                                                    {
                                                                        index: true,
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <TrialGroupLaboratoriesPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                        handle: { help: { topicKey: "trial-group-laboratories" } }
                                                                    },
                                                                    {
                                                                        path: "create",
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <TrialGroupLaboratoryPage
                                                                                    kind="trialGroup"
                                                                                    routeOrientation="trial"
                                                                                    mode="create"
                                                                                />
                                                                            </React.Suspense>
                                                                        ),
                                                                        handle: { help: { topicKey: "trial-group-laboratory" } }
                                                                    },
                                                                    {
                                                                        path: ":trialGroupLaboratoryId",
                                                                        handle: { help: null },
                                                                        children:
                                                                            [
                                                                                {
                                                                                    index: true,
                                                                                    element:
                                                                                        (
                                                                                            <Navigate replace to="details" />
                                                                                        ),
                                                                                        handle: { help: null }
                                                                                },
                                                                                {
                                                                                    path: "details",
                                                                                    element: (
                                                                                        <React.Suspense fallback={<div>Loading...</div>}>
                                                                                            <TrialGroupLaboratoryPage
                                                                                                kind="trialGroup"
                                                                                                routeOrientation="trial"
                                                                                                mode="edit"
                                                                                            />
                                                                                        </React.Suspense>
                                                                                    ),
                                                                                    handle: { help: { topicKey: "trial-group-laboratory" } }
                                                                                },
                                                                                {
                                                                                    path: "personnel",
                                                                                    handle: { help: null },
                                                                                    children:
                                                                                        [
                                                                                            {
                                                                                                index: true,
                                                                                                element: (
                                                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                                                        <TrialGroupLaboratoryPersonnelsPage />
                                                                                                    </React.Suspense>
                                                                                                ),
                                                                                                handle: { help: { topicKey: "trial-group-laboratory-personnels" } }
                                                                                            },
                                                                                            {
                                                                                                path: "create",
                                                                                                element: (
                                                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                                                        <TrialGroupLaboratoryPersonnelPage />
                                                                                                    </React.Suspense>
                                                                                                ),
                                                                                                handle: { help: { topicKey: "trial-group-laboratory-personnel" } }
                                                                                            },
                                                                                            {
                                                                                                path: ":trialGroupLaboratoryPersonnelId",
                                                                                                handle: { help: null },
                                                                                                children:
                                                                                                    [
                                                                                                        {
                                                                                                            index: true,
                                                                                                            element:
                                                                                                                (
                                                                                                                    <Navigate replace to="details" />
                                                                                                                ),
                                                                                                                handle: { help: null }
                                                                                                        },
                                                                                                        {
                                                                                                            path: "details",
                                                                                                            element: (
                                                                                                                <React.Suspense fallback={<div>Loading...</div>}>
                                                                                                                    <TrialGroupLaboratoryPersonnelPage />
                                                                                                                </React.Suspense>
                                                                                                            ),
                                                                                                            handle: { help: { topicKey: "trial-group-laboratory-personnel" } }
                                                                                                        },
                                                                                                    ],
                                                                                            },
                                                                                        ],
                                                                                },
                                                                                {
                                                                                    path: "finance",
                                                                                    element: (
                                                                                        <React.Suspense fallback={<div>Loading...</div>}>
                                                                                            <TrialGroupLaboratoryFinancePage />
                                                                                        </React.Suspense>
                                                                                    ),
                                                                                    handle: { help: { topicKey: "trial-group-laboratory-finance" } }
                                                                                },
                                                                                {
                                                                                    path: "contracts",
                                                                                    handle: { help: null },
                                                                                    children:
                                                                                        [
                                                                                            {
                                                                                                index: true,
                                                                                                element: (
                                                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                                                        <TrialGroupLaboratoryContractsPage />
                                                                                                    </React.Suspense>
                                                                                                ),
                                                                                                handle: { help: { topicKey: "trial-group-laboratory-contracts" } }
                                                                                            },
                                                                                            {
                                                                                                path: "create",
                                                                                                element: (
                                                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                                                        <TrialGroupLaboratoryContractPage />
                                                                                                    </React.Suspense>
                                                                                                ),
                                                                                                handle: { help: { topicKey: "trial-group-laboratory-contract" } }
                                                                                            },
                                                                                            {
                                                                                                path: ":trialGroupLaboratoryContractId",
                                                                                                handle: { help: null },
                                                                                                children:
                                                                                                    [
                                                                                                        {
                                                                                                            index: true,
                                                                                                            element:
                                                                                                                (
                                                                                                                    <Navigate replace to="details" />
                                                                                                                ),
                                                                                                                handle: { help: null }
                                                                                                        },
                                                                                                        {
                                                                                                            path: "details",
                                                                                                            element: (
                                                                                                                <React.Suspense fallback={<div>Loading...</div>}>
                                                                                                                    <TrialGroupLaboratoryContractPage />
                                                                                                                </React.Suspense>
                                                                                                            ),
                                                                                                            handle: { help: { topicKey: "trial-group-laboratory-contract" } }
                                                                                                        },
                                                                                                    ],
                                                                                            },
                                                                                        ],
                                                                                },
                                                                            ],
                                                                    },
                                                                ],
                                                            },
                                                            {
                                                                path: "finance",
                                                                element: (
                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                        <TrialGroupFinancePage />
                                                                    </React.Suspense>
                                                                ),
                                                                handle: { help: { topicKey: "trial-group-finance" } }
                                                            },
                                                            {
                                                                path: "contracts",
                                                                handle: { help: null },
                                                                children: [
                                                                    {
                                                                        index: true,
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <TrialGroupContractsPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                        handle: { help: { topicKey: "trial-group-contracts" } }
                                                                    },
                                                                    {
                                                                        path: "create",
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <TrialGroupContractPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                        handle: { help: { topicKey: "trial-group-contract" } }
                                                                    },
                                                                    {
                                                                        path: ":trialGroupContractId",
                                                                        handle: { help: null },
                                                                        children:
                                                                            [
                                                                                {
                                                                                    index: true,
                                                                                    element:
                                                                                        (
                                                                                            <Navigate replace to="details" />
                                                                                        ),
                                                                                        handle: { help: null }
                                                                                },
                                                                                {
                                                                                    path: "details",
                                                                                    element: (
                                                                                        <React.Suspense fallback={<div>Loading...</div>}>
                                                                                            <TrialGroupContractPage />
                                                                                        </React.Suspense>
                                                                                    ),
                                                                                    handle: { help: { topicKey: "trial-group-contract" } }
                                                                                },
                                                                            ],
                                                                    },
                                                                ],
                                                            },
                                                        ],
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                    {
                                        path: ":trialGroupId",
                                        handle: { help: { topicKey: "dashboard" } },
                                        children: [
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <TrialGroupPage />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "trial-group" } }
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "datasharing",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <TrialDoiPage />
                                    </React.Suspense>
                                ),
                                handle: { help: { topicKey: "trial-doi" } }
                            },
                            {
                                path: "website",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <TrialWebsitePage />
                                    </React.Suspense>
                                ),
                                handle: { help: { topicKey: "trial-website" } }
                            },
                            {
                                path: "finance",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <TrialFinancePage />
                                    </React.Suspense>
                                ),
                                handle: { help: { topicKey: "trial-finance" } }
                            },
                            {
                                path: "labs",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <TrialLaboratoriesPage />
                                    </React.Suspense>
                                ),
                                handle: { help: { topicKey: "trial-laboratories" } }
                            },
                        ],
                    },
                    {
                        path: ":projectId",
                        handle: { help: null },
                        children: [
                            {
                                path: "project",
                                handle: { help: null },
                                children: [
                                    {
                                        path: "details",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ProjectPage context="trial" mode="edit" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "project" } }
                                    },
                                    {
                                        path: "investigators",
                                        handle: { help: null },
                                        children: [
                                            {
                                                path: ":projectInvestigatorId",
                                                handle: { help: null },
                                                children: [
                                                    {
                                                        index: true,
                                                        element: (
                                                            <Navigate replace to="details" />
                                                        ),
                                                        handle: { help: null }
                                                    },
                                                    {
                                                        path: "details",
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <ProjectInvestigatorPage context="trial" mode="edit" />
                                                            </React.Suspense>
                                                        ),
                                                        handle: { help: { topicKey: "project-investigator" } }
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                    {
                                        path: "discussions",
                                        handle: { help: null },
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <DiscussionsPage />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "discussions" } }
                                            },
                                            {
                                                path: "create",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <DiscussionPage orientation="trial" mode="create" />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "discussion" } }
                                            },
                                            {
                                                path: ":discussionId",
                                                handle: { help: null },
                                                children: [
                                                    {
                                                        index: true,
                                                        element: (
                                                            <Navigate replace to="details" />
                                                        ),
                                                        handle: { help: null }
                                                    },
                                                    {
                                                        path: "details",
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <DiscussionPage orientation="trial" mode="edit" />
                                                            </React.Suspense>
                                                        ),
                                                        handle: { help: { topicKey: "discussion" } }
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                    {
                                        path: "items",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ProjectItemsPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "project-items" } }
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                path: "committees",
                handle: { help: null },
                children: [
                    {
                        index: true,
                        element: (
                            <React.Suspense fallback={<OverlayLoading />}>
                                <CommitteesPage />
                            </React.Suspense>
                        ),
                        handle: { help: { topicKey: "committees" } }
                    },
                    {
                        path: "create",
                        element: (
                            <React.Suspense fallback={<OverlayLoading />}>
                                <CommitteePage />
                            </React.Suspense>
                        ),
                        handle: { help: { topicKey: "committee" } }
                    },
                    {
                        path: ":committeeId",
                        handle: { help: null },
                        children: [
                            {
                                index: true,
                                element: <Navigate replace to="details" />,
                                handle: { help: null }
                            },
                            {
                                path: "details",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <CommitteePage />
                                    </React.Suspense>
                                ),
                                handle: { help: { topicKey: "committee" } }
                            },
                            {
                                path: "meetings",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <CommitteeMeetingsPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "committee-meetings" } }
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <CommitteeMeetingPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "committee-meeting" } }
                                    },
                                    {
                                        path: ":committeeMeetingId",
                                        handle: { help: null },
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                handle: { help: null }
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <CommitteeMeetingPage />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "committee-meeting" } }
                                            },
                                            {
                                                path: "attendances",
                                                handle: { help: null },
                                                children: [
                                                    {
                                                        index: true,
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <CommitteeMeetingAttendancesPage />
                                                            </React.Suspense>
                                                        ),
                                                        handle: { help: { topicKey: "committee-meeting-attendances" } }
                                                    },
                                                    {
                                                        path: "create",
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <CommitteeMeetingAttendancePage />
                                                            </React.Suspense>
                                                        ),
                                                        handle: { help: { topicKey: "committee-meeting-attendance" } }
                                                    },
                                                    {
                                                        path: ":committeeMeetingAttendanceId",
                                                        handle: { help: null },
                                                        children: [
                                                            {
                                                                index: true,
                                                                element: (
                                                                    <Navigate replace to="details" />
                                                                ),
                                                                handle: { help: null }
                                                            },
                                                            {
                                                                path: "details",
                                                                element: (
                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                        <CommitteeMeetingAttendancePage />
                                                                    </React.Suspense>
                                                                ),
                                                                handle: { help: { topicKey: "committee-meeting-attendance" } }
                                                            },
                                                        ],
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "members",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <CommitteeMembersPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "committee-members" } }
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <CommitteeMemberPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "committee-member" } }
                                    },
                                    {
                                        path: ":committeeMemberId",
                                        handle: { help: null },
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                handle: { help: null }
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <CommitteeMemberPage />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "committee-member" } }
                                            },
                                            {
                                                path: "roles",
                                                handle: { help: null },
                                                children: [
                                                    {
                                                        index: true,
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <CommitteeMemberRolesPage />
                                                            </React.Suspense>
                                                        ),
                                                        handle: { help: { topicKey: "committee-member-roles" } }
                                                    },
                                                    {
                                                        path: "create",
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <CommitteeMemberRolePage />
                                                            </React.Suspense>
                                                        ),
                                                        handle: { help: { topicKey: "committee-member-role" } }
                                                    },
                                                    {
                                                        path: ":committeeMemberRoleId",
                                                        handle: { help: null },
                                                        children: [
                                                            {
                                                                index: true,
                                                                element: (
                                                                    <Navigate replace to="details" />
                                                                ),
                                                                handle: { help: null }
                                                            },
                                                            {
                                                                path: "details",
                                                                element: (
                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                        <CommitteeMemberRolePage />
                                                                    </React.Suspense>
                                                                ),
                                                                handle: { help: { topicKey: "committee-member-role" } }
                                                            },
                                                        ],
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "regularinvitees",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <CommitteeRegularInviteesPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "committee-regular-invitees" } }
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <CommitteeRegularInviteePage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "committee-regular-invitee" } }
                                    },
                                    {
                                        path: ":committeeRegularInviteeId",
                                        handle: { help: null },
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                handle: { help: null }
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <CommitteeRegularInviteePage />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "committee-regular-invitee" } }
                                            },
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                path: "groups",
                handle: { help: null },
                children: [
                    {
                        index: true,
                        element: (
                            <React.Suspense fallback={<OverlayLoading />}>
                                <GroupsPage />
                            </React.Suspense>
                        ),
                        handle: { help: { topicKey: "groups" } }
                    },
                    {
                        path: "create",
                        element: (
                            <React.Suspense fallback={<OverlayLoading />}>
                                <GroupPage mode="create" />
                            </React.Suspense>
                        ),
                        handle: { help: { topicKey: "group" } }
                    },
                    {
                        path: ":groupId",
                        handle: { help: null },
                        children: [
                            {
                                index: true,
                                element: <Navigate replace to="details" />,
                                handle: { help: null }
                            },
                            {
                                path: "details",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <GroupPage mode="edit" />
                                    </React.Suspense>
                                ),
                                handle: { help: { topicKey: "group" } }
                            },
                            {
                                path: "trials",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <GroupTrialsPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "group-trials" } }
                                    },
                                    {
                                        path: ":trialId",
                                        handle: { help: null },
                                        children: [
                                            {
                                                path: "trialgroup",
                                                handle: { help: null },
                                                children: [
                                                    {
                                                        path: ":trialGroupId",
                                                        handle: { help: { topicKey: "dashboard" } },
                                                        children: [
                                                            {
                                                                index: true,
                                                                element: (
                                                                    <Navigate replace to="details" />
                                                                ),
                                                                handle: { help: null }
                                                            },
                                                            {
                                                                path: "details",
                                                                element: (
                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                        <TrialGroupPage />
                                                                    </React.Suspense>
                                                                ),
                                                                handle: { help: { topicKey: "trial-group" } }
                                                            },
                                                            {
                                                                path: "personnel",
                                                                handle: { help: null },
                                                                children: [
                                                                    {
                                                                        index: true,
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <TrialGroupPersonnelsPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                        handle: { help: { topicKey: "trial-group-personnels" } }
                                                                    },
                                                                    {
                                                                        path: "create",
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <TrialGroupPersonnelPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                        handle: { help: { topicKey: "trial-group-personnel" } }
                                                                    },
                                                                    {
                                                                        path: ":trialPersonnelId",
                                                                        handle: { help: null },
                                                                        children:
                                                                            [
                                                                                {
                                                                                    index: true,
                                                                                    element:
                                                                                        (
                                                                                            <Navigate replace to="details" />
                                                                                        ),
                                                                                        handle: { help: null }
                                                                                },
                                                                                {
                                                                                    path: "details",
                                                                                    element: (
                                                                                        <React.Suspense fallback={<div>Loading...</div>}>
                                                                                            <TrialGroupPersonnelPage />
                                                                                        </React.Suspense>
                                                                                    ),
                                                                                    handle: { help: { topicKey: "trial-group-personnel" } }
                                                                                },
                                                                            ],
                                                                    },
                                                                ],
                                                            },
                                                            {
                                                                path: "items",
                                                                element: (
                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                        <TrialGroupItemsPage />
                                                                    </React.Suspense>
                                                                ),
                                                                handle: { help: { topicKey: "trial-group-items" } }
                                                            },
                                                            {
                                                                path: "labs",
                                                                handle: { help: null },
                                                                children: [
                                                                    {
                                                                        index: true,
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <TrialGroupLaboratoriesPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                        handle: { help: { topicKey: "trial-group-laboratories" } }
                                                                    },
                                                                    {
                                                                        path: "create",
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <TrialGroupLaboratoryPage
                                                                                    kind="trialGroup"
                                                                                    routeOrientation="group"
                                                                                    mode="create"
                                                                                />
                                                                            </React.Suspense>
                                                                        ),
                                                                        handle: { help: { topicKey: "trial-group-laboratory" } }
                                                                    },
                                                                    {
                                                                        path: ":trialGroupLaboratoryId",
                                                                        handle: { help: null },
                                                                        children:
                                                                            [
                                                                                {
                                                                                    index: true,
                                                                                    element:
                                                                                        (
                                                                                            <Navigate replace to="details" />
                                                                                        ),
                                                                                        handle: { help: null }
                                                                                },
                                                                                {
                                                                                    path: "details",
                                                                                    element: (
                                                                                        <React.Suspense fallback={<div>Loading...</div>}>
                                                                                            <TrialGroupLaboratoryPage
                                                                                                kind="trialGroup"
                                                                                                routeOrientation="group"
                                                                                                mode="edit"
                                                                                            />
                                                                                        </React.Suspense>
                                                                                    ),
                                                                                    handle: { help: { topicKey: "trial-group-laboratory" } }
                                                                                },
                                                                                {
                                                                                    path: "personnel",
                                                                                    handle: { help: null },
                                                                                    children:
                                                                                        [
                                                                                            {
                                                                                                index: true,
                                                                                                element: (
                                                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                                                        <TrialGroupLaboratoryPersonnelsPage />
                                                                                                    </React.Suspense>
                                                                                                ),
                                                                                                handle: { help: { topicKey: "trial-group-laboratory-personnels" } }
                                                                                            },
                                                                                            {
                                                                                                path: "create",
                                                                                                element: (
                                                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                                                        <TrialGroupLaboratoryPersonnelPage />
                                                                                                    </React.Suspense>
                                                                                                ),
                                                                                                handle: { help: { topicKey: "trial-group-laboratory-personnel" } }
                                                                                            },
                                                                                            {
                                                                                                path: ":trialGroupLaboratoryPersonnelId",
                                                                                                handle: { help: null },
                                                                                                children:
                                                                                                    [
                                                                                                        {
                                                                                                            index: true,
                                                                                                            element:
                                                                                                                (
                                                                                                                    <Navigate replace to="details" />
                                                                                                                ),
                                                                                                                handle: { help: null }
                                                                                                        },
                                                                                                        {
                                                                                                            path: "details",
                                                                                                            element: (
                                                                                                                <React.Suspense fallback={<div>Loading...</div>}>
                                                                                                                    <TrialGroupLaboratoryPersonnelPage />
                                                                                                                </React.Suspense>
                                                                                                            ),
                                                                                                            handle: { help: { topicKey: "trial-group-laboratory-personnel" } }
                                                                                                        },
                                                                                                    ],
                                                                                            },
                                                                                        ],
                                                                                },
                                                                                {
                                                                                    path: "finance",
                                                                                    element: (
                                                                                        <React.Suspense fallback={<div>Loading...</div>}>
                                                                                            <TrialGroupLaboratoryFinancePage />
                                                                                        </React.Suspense>
                                                                                    ),
                                                                                    handle: { help: { topicKey: "trial-group-laboratory-finance" } }
                                                                                },
                                                                                {
                                                                                    path: "contracts",
                                                                                    handle: { help: null },
                                                                                    children:
                                                                                        [
                                                                                            {
                                                                                                index: true,
                                                                                                element: (
                                                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                                                        <TrialGroupLaboratoryContractsPage />
                                                                                                    </React.Suspense>
                                                                                                ),
                                                                                                handle: { help: { topicKey: "trial-group-laboratory-contracts" } }
                                                                                            },
                                                                                            {
                                                                                                path: "create",
                                                                                                element: (
                                                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                                                        <TrialGroupLaboratoryContractPage />
                                                                                                    </React.Suspense>
                                                                                                ),
                                                                                                handle: { help: { topicKey: "trial-group-laboratory-contract" } }
                                                                                            },
                                                                                            {
                                                                                                path: ":trialGroupLaboratoryContractId",
                                                                                                handle: { help: null },
                                                                                                children:
                                                                                                    [
                                                                                                        {
                                                                                                            index: true,
                                                                                                            element:
                                                                                                                (
                                                                                                                    <Navigate replace to="details" />
                                                                                                                ),
                                                                                                                handle: { help: null }
                                                                                                        },
                                                                                                        {
                                                                                                            path: "details",
                                                                                                            element: (
                                                                                                                <React.Suspense fallback={<div>Loading...</div>}>
                                                                                                                    <TrialGroupLaboratoryContractPage />
                                                                                                                </React.Suspense>
                                                                                                            ),
                                                                                                            handle: { help: { topicKey: "trial-group-laboratory-contract" } }
                                                                                                        },
                                                                                                    ],
                                                                                            },
                                                                                        ],
                                                                                },
                                                                            ],
                                                                    },
                                                                ],
                                                            },
                                                            {
                                                                path: "finance",
                                                                element: (
                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                        <TrialGroupFinancePage />
                                                                    </React.Suspense>
                                                                ),
                                                                handle: { help: { topicKey: "trial-group-finance" } }
                                                            },
                                                            {
                                                                path: "contracts",
                                                                handle: { help: null },
                                                                children: [
                                                                    {
                                                                        index: true,
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <TrialGroupContractsPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                        handle: { help: { topicKey: "trial-group-contracts" } }
                                                                    },
                                                                    {
                                                                        path: "create",
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <TrialGroupContractPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                        handle: { help: { topicKey: "trial-group-contract" } }
                                                                    },
                                                                    {
                                                                        path: ":trialGroupContractId",
                                                                        handle: { help: null },
                                                                        children:
                                                                            [
                                                                                {
                                                                                    index: true,
                                                                                    element:
                                                                                        (
                                                                                            <Navigate replace to="details" />
                                                                                        ),
                                                                                        handle: { help: null }
                                                                                },
                                                                                {
                                                                                    path: "details",
                                                                                    element: (
                                                                                        <React.Suspense fallback={<div>Loading...</div>}>
                                                                                            <TrialGroupContractPage />
                                                                                        </React.Suspense>
                                                                                    ),
                                                                                    handle: { help: { topicKey: "trial-group-contract" } }
                                                                                },
                                                                            ],
                                                                    },
                                                                ],
                                                            },
                                                        ],
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "notes",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <GroupNotesPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "group-notes" } }
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <NotePage kind="group" mode="create" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "note" } }
                                    },
                                    {
                                        path: ":noteId",
                                        handle: { help: null },
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                handle: { help: null }
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <NotePage kind="group" mode="edit" />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "note" } }
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "personnel",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <GroupPersonnelsPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "group-personnels" } }
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <GroupPersonnelPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "group-personnel" } }
                                    },
                                    {
                                        path: ":trialPersonnelId",
                                        handle: { help: null },
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                handle: { help: null }
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <GroupPersonnelPage />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "group-personnel" } }
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "contracts",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <GroupContractPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "group-contract" } }
                                    },
                                ],
                            },
                            {
                                path: "finances",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <GroupFinancePage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "group-finance" } }
                                    },
                                ],
                            },
                            {
                                path: "items",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <GroupItemsPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "group-items" } }
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                path: "personnel",
                handle: { help: null },
                children: [
                    {
                        index: true,
                        element: (
                            <React.Suspense fallback={<OverlayLoading />}>
                                <PersonnelsPage />
                            </React.Suspense>
                        ),
                        handle: { help: { topicKey: "personnels" } }
                    },
                    {
                        path: "create",
                        element: (
                            <React.Suspense fallback={<OverlayLoading />}>
                                <PersonnelPage />
                            </React.Suspense>
                        ),
                        handle: { help: { topicKey: "personnel" } }
                    },
                    {
                        path: "byroles",
                        element: (
                            <React.Suspense fallback={<OverlayLoading />}>
                                <PersonnelByRolesPage />
                            </React.Suspense>
                        ),
                        handle: { help: { topicKey: "personnel-by-roles" } }
                    },
                    {
                        path: ":personnelId",
                        handle: { help: null },
                        children: [
                            {
                                index: true,
                                element: <Navigate replace to="details" />,
                                handle: { help: null }
                            },
                            {
                                path: "details",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <PersonnelPage />
                                    </React.Suspense>
                                ),
                                handle: { help: { topicKey: "personnel" } }
                            },
                            {
                                path: "trainingcertifications",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <PersonnelTrainingCertificationPage />
                                    </React.Suspense>
                                ),
                                handle: { help: { topicKey: "personnel-training-certification" } }
                            },
                            {
                                path: "roles",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <PersonnelRolesPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "personnel-roles" } }
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <PersonnelRolePage mode="create" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "personnel-role" } }
                                    },
                                    {
                                        path: ":personnelRoleId",
                                        handle: { help: null },
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                handle: { help: null }
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <PersonnelRolePage mode="edit" />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "personnel-role" } }
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "permissions",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <PersonnelPermissionsPage />
                                    </React.Suspense>
                                ),
                                handle: { help: { topicKey: "personnel-permissions" } }
                            },
                            {
                                path: "activedirectory",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <PersonnelActiveDirectoryPage />
                                    </React.Suspense>
                                ),
                                handle: { help: { topicKey: "personnel-active-directory" } }
                            },
                            {
                                path: "notes",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <PersonnelNotesPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "personnel-notes" } }
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <NotePage kind="personnel" mode="create" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "note" } }
                                    },
                                    {
                                        path: ":noteId",
                                        handle: { help: null },
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                handle: { help: null }
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <NotePage kind="personnel" mode="edit" />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "note" } }
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "fellowship",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <FellowshipsPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "fellowships" } }
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <FellowshipPage mode="create" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "fellowship" } }
                                    },
                                    {
                                        path: ":fellowshipId",
                                        handle: { help: null },
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                handle: { help: null }
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <FellowshipPage mode="edit" />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "fellowship" } }
                                            },
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                path: "projects",
                handle: { help: null },
                children: [
                    {
                        index: true,
                        element: (
                            <React.Suspense fallback={<OverlayLoading />}>
                                <ProjectsPage />
                            </React.Suspense>
                        ),
                        handle: { help: { topicKey: "projects" } }
                    },
                    {
                        path: "create",
                        element: (
                            <React.Suspense fallback={<OverlayLoading />}>
                                <ProjectPage context="project" mode="create" />
                            </React.Suspense>
                        ),
                        handle: { help: { topicKey: "project" } }
                    },
                    {
                        path: ":projectId",
                        handle: { help: null },
                        children: [
                            {
                                index: true,
                                element: <Navigate replace to="details" />,
                                handle: { help: null }
                            },
                            {
                                path: "details",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <ProjectPage context="project" mode="edit" />
                                    </React.Suspense>
                                ),
                                handle: { help: { topicKey: "project" } }
                            },
                            {
                                path: "items",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <ProjectItemsPage />
                                    </React.Suspense>
                                ),
                                handle: { help: { topicKey: "project-items" } }
                            },
                            {
                                path: "investigators",
                                handle: { help: null },
                                children: [
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ProjectInvestigatorPage context="project" mode="create" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "project-investigator" } }
                                    },
                                    {
                                        path: ":projectInvestigatorId",
                                        handle: { help: null },
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                handle: { help: null }
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <ProjectInvestigatorPage context="project" mode="edit" />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "project-investigator" } }
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "discussions",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <DiscussionsPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "discussions" } }
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <DiscussionPage orientation="project" mode="create" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "discussion" } }
                                    },
                                    {
                                        path: ":discussionId",
                                        handle: { help: null },
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                handle: { help: null }
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <DiscussionPage orientation="project" mode="edit" />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "discussion" } }
                                            },
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                path: "institutions",
                handle: { help: null },
                children: [
                    {
                        index: true,
                        element: (
                            <React.Suspense fallback={<OverlayLoading />}>
                                <InstitutionsPage />
                            </React.Suspense>
                        ),
                        handle: { help: { topicKey: "institutions" } }
                    },
                    {
                        path: "create",
                        element: (
                            <React.Suspense fallback={<OverlayLoading />}>
                                <InstitutionPage mode="create" />
                            </React.Suspense>
                        ),
                        handle: { help: { topicKey: "institution" } }
                    },
                    {
                        path: ":institutionId",
                        handle: { help: null },
                        children: [
                            {
                                index: true,
                                element: <Navigate replace to="details" />,
                                handle: { help: null }
                            },
                            {
                                path: "details",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <InstitutionPage mode="edit" />
                                    </React.Suspense>
                                ),
                                handle: { help: { topicKey: "institution" } }
                            },
                            {
                                path: "institutionitems",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <InstitutionItemsPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "institution-items" } }
                                    },
                                ],
                            },
                            {
                                path: "personnel",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <InstitutionPersonnelsPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "institution-personnels" } }
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <InstitutionPersonnelPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "institution-personnel" } }
                                    },
                                    {
                                        path: ":trialPersonnelId",
                                        handle: { help: null },
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                handle: { help: null }
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <InstitutionPersonnelPage />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "institution-personnel" } }
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "labs",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <InstitutionLaboratoriesPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "institution-laboratories" } }
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <InstitutionLaboratoryPage mode="create" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "institution-laboratory" } }
                                    },
                                    {
                                        path: ":institutionLaboratoryId",
                                        handle: { help: null },
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                handle: { help: null }
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <InstitutionLaboratoryPage mode="edit" />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "institution-laboratory" } }
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "notes",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <InstitutionNotesPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "institution-notes" } }
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <NotePage kind="institution" mode="create" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "note" } }
                                    },
                                    {
                                        path: ":noteId",
                                        handle: { help: null },
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                handle: { help: null }
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <NotePage kind="institution" mode="edit" />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "note" } }
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "procedures",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <InstitutionProceduresPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "institution-procedures" } }
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <InstitutionProcedurePage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "institution-procedure" } }
                                    },
                                    {
                                        path: ":institutionProcedureId",
                                        handle: { help: null },
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                handle: { help: null }
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <InstitutionProcedurePage />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "institution-procedure" } }
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "finance",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <InstitutionFinancePage />
                                    </React.Suspense>
                                ),
                                handle: { help: { topicKey: "institution-finance" } }
                            },
                            {
                                path: "contract",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <InstitutionContractPage />
                                    </React.Suspense>
                                ),
                                handle: { help: { topicKey: "institution-contract" } }
                            },
                            {
                                path: "codes",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <InstitutionCodesPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "institution-codes" } }
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <InstitutionCodePage mode="create" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "institution-code" } }
                                    },
                                    {
                                        path: ":institutionCodeId",
                                        handle: { help: { topicKey: "dashboard" } },
                                        children: [
                                            {
                                                index: true,
                                                element: <Navigate replace to="details" />,
                                                handle: { help: null }
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <InstitutionCodePage mode="edit" />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "institution-code" } }
                                            }
                                        ],
                                    },
                                ]
                            },
                            {
                                path: "trials",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <InstitutionTrialsPage />
                                    </React.Suspense>
                                ),
                                handle: { help: { topicKey: "institution-trials" } }
                            }
                        ],
                    },
                ],
            },
            {
                path: "labs",
                handle: { help: null },
                children: [
                    {
                        index: true,
                        element: (
                            <React.Suspense fallback={<OverlayLoading />}>
                                <LabsPage />
                            </React.Suspense>
                        ),
                        handle: { help: { topicKey: "labs" } }
                    },
                    {
                        path: "create",
                        element: (
                            <React.Suspense fallback={<OverlayLoading />}>
                                <LabPage mode="create" />
                            </React.Suspense>
                        ),
                        handle: { help: { topicKey: "lab" } }
                    },
                    {
                        path: ":laboratoryId",
                        handle: { help: null },
                        children: [
                            {
                                index: true,
                                element: <Navigate replace to="details" />,
                                handle: { help: null }
                            },
                            {
                                path: "details",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <LabPage mode="edit" />
                                    </React.Suspense>
                                ),
                                handle: { help: { topicKey: "lab" } }
                            },
                            {
                                path: "departments",
                                handle: { help: { topicKey: "lab-departments" } },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <LabDepartmentsPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "lab-departments" } }
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <LabDepartmentPage mode="create" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "lab-department" } }
                                    },
                                    {
                                        path: ":laboratoryDepartmentId",
                                        handle: { help: null },
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                handle: { help: null }
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <LabDepartmentPage mode="edit" />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "lab-department" } }
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "personnel",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <LabPersonnelsPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "lab-personnels" } }
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <LabPersonnelPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "lab-personnel" } }
                                    },
                                    {
                                        path: ":laboratoryPersonnelId",
                                        handle: { help: null },
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                handle: { help: null }
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <LabPersonnelPage />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "lab-personnel" } }
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "institutions",
                                handle: { help: null },
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <LabInstitutionsPage />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "lab-institutions" } }
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <LabInstitutionPage mode="create" />
                                            </React.Suspense>
                                        ),
                                        handle: { help: { topicKey: "lab-institution" } }
                                    },
                                    {
                                        path: ":institutionLaboratoryId",
                                        handle: { help: null },
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                handle: { help: null }
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <LabInstitutionPage mode="edit" />
                                                    </React.Suspense>
                                                ),
                                                handle: { help: { topicKey: "lab-institutions" } }
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "finance",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <LabFinancePage />
                                    </React.Suspense>
                                ),
                                handle: { help: { topicKey: "lab-finance" } }
                            },
                            {
                                path: "contract",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <LabContractPage />
                                    </React.Suspense>
                                ),
                                handle: { help: { topicKey: "lab-contract" } }
                            },
                        ],
                    },
                ],
            },
        ],
    },

];
